import clsx from 'clsx'
import map from 'lodash/map'
import { useRouter } from 'next/router'
import React, { FC, useState } from 'react'

import AngleRight from '@Assets/SVG/AngleRight'
import { Button } from '@Components/Button'
import { SVGIcon } from '@Components/Icons/SVGIcon'
import { toTitleCase } from '@Domains/Checkout/Steps/Delivery/utils/TitleCase.utils'
import { useSplitTest } from '@Globals/SplitTests/Hooks'
import { useTranslation } from '@Globals/Translations'
import { RoundCheckbox } from '@Shared/Components/Inputs/Checkbox/RoundCheckbox'

import styles from './DeliveryDropPointItem.module.scss'

interface DropPointMapItemProps {
  city: string
  distance: number
  handleDropPointSelection: (id: string) => void
  id: string
  name: string
  openingHours: string[]
  postalCode: string
  selectedDropPointId?: string
  streetName: string
}

export const DeliveryDropPointItem: FC<DropPointMapItemProps> = ({
  city,
  distance,
  handleDropPointSelection,
  id,
  name,
  openingHours,
  postalCode,
  selectedDropPointId,
  streetName,
}) => {
  const cofDesignV1 = useSplitTest('cof_design_v1', 'b')
  const { t } = useTranslation()
  const { locale } = useRouter()
  const [showOpeningHours, setShowOpeningHours] = useState<string[]>([])

  const toggleOpeningHours = (toggleId: string) => {
    if (showOpeningHours.includes(toggleId)) {
      setShowOpeningHours(showOpeningHours.filter(item => item !== toggleId))
    } else {
      setShowOpeningHours([...showOpeningHours, toggleId])
    }
  }

  if (cofDesignV1) {
    return (
      <button
        key={id}
        aria-label={`Select ${toTitleCase(name)} at ${toTitleCase(streetName)}, ${toTitleCase(`${city} ${postalCode}`)}`}
        className={clsx(styles.deliveryDropPointItem, {
          [styles.active]: id === selectedDropPointId,
        }, styles.cofDesignV1)}
        onClick={() => handleDropPointSelection(id)}
        type="button"
      >
        <RoundCheckbox
          checked={id === selectedDropPointId}
          className={styles.checkbox}
          tabIndex={-1}
        />
        <strong>{toTitleCase(name)}</strong>
        <div className={styles.distance}>
          {`${t('checkout:COF_DISTANCE')}: `}
          {(distance / 1000).toLocaleString(locale, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          })}
          {t('checkout:KILOMETER_SHORT')}
        </div>
        <div>{toTitleCase(streetName)}</div>
        <div>
          {toTitleCase(`${city} ${postalCode}`)}
        </div>

        <button
          className={clsx(styles.openHoursTrigger, {
            [styles.active]: showOpeningHours.includes(id),
          })}
          onClick={(e) => {
            e.stopPropagation()
            toggleOpeningHours(id)
          }}
          tabIndex={0}
          type="button"
        >
          {t('checkout:COF_OPENING_HOURS')}
          <SVGIcon icon={AngleRight} size="12px" />
        </button>

        {showOpeningHours.includes(id) && (
          <div className={styles.openingList}>
            {map(openingHours, openingHour => (
              <div
                key={openingHour}
                className={styles.openingHour}
              >
                {openingHour}
              </div>
            ))}
          </div>
        )}
      </button>
    )
  }

  return (
    <div
      key={id}
      className={clsx(styles.deliveryDropPointItem, {
        [styles.active]: id === selectedDropPointId,
      })}
    >
      <strong>{toTitleCase(name)}</strong>
      <div>{toTitleCase(streetName)}</div>
      <div>
        {toTitleCase(`${city} ${postalCode}`)}
      </div>

      <div className={styles.distance}>
        {`${t('checkout:COF_DISTANCE')}: `}
        {(distance / 1000).toLocaleString(locale, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })}
        {t('checkout:KILOMETER_SHORT')}
      </div>

      <button
        className={clsx(styles.openHoursTrigger, {
          [styles.active]: showOpeningHours.includes(id),
        })}
        onClick={() => toggleOpeningHours(id)}
        type="button"
      >
        {t('checkout:COF_OPENING_HOURS')}
        <SVGIcon icon={AngleRight} size="12px" />
      </button>

      {showOpeningHours.includes(id) && (
        <div className={styles.openingList}>
          {map(openingHours, openingHour => (
            <div
              key={openingHour}
              className={styles.openingHour}
            >
              {openingHour}
            </div>
          ))}
        </div>
      )}
      <Button
        className={styles.apply}
        onClick={() => handleDropPointSelection(id)}
      >
        {t('checkout:COF_SELECT')}
      </Button>
    </div>
  )
}
